import { type ApolloError, type DocumentNode, gql, useMutation, useQuery, useReactiveVar } from '@apollo/client';
import OfflineBoltIcon from '@mui/icons-material/OfflineBolt';
import { Button } from '@mui/material';
import Box from '@mui/material/Box/Box';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { Link } from '@tanstack/react-router';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import type React from 'react';
import { useEffect, useState } from 'react';
import AkomiDialog from 'src/components/dialogs/AkomiDialog';
import AkomiDialogActions from 'src/components/dialogs/AkomiDialogActions';
import { API_PATH, HIRES_FORMAT_SLUG, MARKER_SUBCLIP_ACTION_TYPE, VIDEO } from 'src/constants';
import { fireAction } from 'src/dynamoQueries/action';
import { AssetNodeType, assetQuery, formatSet } from 'src/dynamoQueries/assets';
import { allJobsBasicQuery, getJobResults, JobNodeType } from 'src/dynamoQueries/jobs';
import viewStateVar from 'src/store/viewState';

const READY = 'ready' as const;
const JOB_COMPLETE = 'job complete' as const;
const JOB_NOT_COMPLETE = 'job not complete' as const;
const JOB_NEEDED = 'job needed' as const;

type DownloadStatus = typeof READY | typeof JOB_COMPLETE | typeof JOB_NOT_COMPLETE | typeof JOB_NEEDED | null;

interface FormatTypeNode {
  slug: string;
  name: string;
}

interface FormatNode {
  id: string;
  filename: string;
  online: boolean;
  presignable: boolean;
  mimetype: string;
  fmtType: FormatTypeNode;
}

interface FormatEdge {
  node: FormatNode;
}

interface AssetPermissions {
  hires: boolean;
}

interface AssetQueryResponse {
  asset: {
    myAssetPermissions: string;
    formatSet: {
      edges: FormatEdge[];
    };
  };
}

interface JobNode {
  id: string;
  status: string;
  createdTimestamp: string;
}

interface JobEdge {
  node: JobNode;
}

interface AllJobsQueryResponse {
  allJobs: {
    edges: JobEdge[];
  };
}

interface GetJobResultResponse {
  getJobResult: {
    result: string;
  };
}

interface FireActionResponse {
  fireAction: {
    success: boolean;
  };
}

interface FireActionVariables {
  actionType: string;
  payload: {
    marker_id: string;
    format_id: string;
  };
}

interface DownloadMarkerModalProps {
  selectedMarkerId: string;
}

const DownloadMarkerModal: React.FC<DownloadMarkerModalProps> = ({ selectedMarkerId }) => {
  const viewState = useReactiveVar(viewStateVar);
  const [selectedFormatId, setSelectedFormatId] = useState<string | null>(null);
  const [selectedFormat, setSelectedFormat] = useState<FormatEdge | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloadStatus, setDownloadStatus] = useState<DownloadStatus>(null);
  const [fSet, setFormatSet] = useState<FormatEdge[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Unused variable from original code - keeping reference to avoid unused warning
  void selectedFormat;

  const isOpen = !!viewState.downloadMarkerDialog.markerId;

  const buildQuery = (): DocumentNode => {
    const asn = AssetNodeType(true) as Record<string, unknown>;
    asn.formatSet = formatSet(true);
    (asn.formatSet as Record<string, unknown>).__args = { downloadable: true };
    const q = assetQuery() as { query: Record<string, unknown> };
    q.query.asset = { ...(q.query.asset as object), ...asn };
    (q.query.asset as Record<string, unknown>).__args = {
      id: viewState.downloadMarkerDialog.assetId,
    };
    const gQuery = jsonToGraphQLQuery(q, { pretty: true });
    return gql(gQuery);
  };

  const buildAllJobsQuery = (markerId: string): DocumentNode => {
    const job = JobNodeType(false) as unknown as Record<string, unknown>;
    job.createdTimestamp = true;
    job.id = true;
    job.status = true;
    const query = allJobsBasicQuery(job as unknown as ReturnType<typeof JobNodeType>) as {
      query: Record<string, unknown>;
    };
    const metadata = {
      marker_id: `${markerId}`,
    };
    (query.query.allJobs as Record<string, unknown>).__args = {
      ...((query.query.allJobs as Record<string, unknown>).__args as object),
      metadata: JSON.stringify(metadata),
    };

    const gQuery = jsonToGraphQLQuery(query, { pretty: true });
    return gql(gQuery);
  };

  const { data: allJobs } = useQuery<AllJobsQueryResponse>(buildAllJobsQuery(selectedMarkerId), {
    skip: !isOpen,
    fetchPolicy: 'no-cache',
  });

  const buildJobResultsMutation = (): DocumentNode => {
    const m = getJobResults();
    const gMutation = jsonToGraphQLQuery(m, { pretty: true });
    return gql(gMutation);
  };

  const [getJobResult, { data: jobResult, error: jobError }] = useMutation<GetJobResultResponse>(
    buildJobResultsMutation(),
  );

  const getFirstJob = (): JobEdge | undefined => {
    return allJobs?.allJobs?.edges[0];
  };

  const onGetJobResult = (): void => {
    const job = getFirstJob();
    getJobResult({ variables: { jobId: job?.node?.id } });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: onClose is stable and intentionally excluded to avoid re-running effect
  useEffect(() => {
    if (jobResult) {
      const atag = document.createElement('a');
      atag.setAttribute('href', jobResult?.getJobResult?.result);
      atag.click();
      atag.remove();
      onClose();
    }
  }, [jobResult]);

  const buildFireAction = (): DocumentNode => {
    const m = fireAction(true);
    const mutation = jsonToGraphQLQuery(m, { pretty: true });
    return gql(mutation);
  };

  const [fireActionMutation, { data: actionResponse, error: actionError }] = useMutation<
    FireActionResponse,
    FireActionVariables
  >(buildFireAction());

  const onFireAction = (): void => {
    const payload = {
      marker_id: viewState.downloadMarkerDialog.markerId,
      format_id: selectedFormatId as string,
    };
    fireActionMutation({
      variables: { actionType: MARKER_SUBCLIP_ACTION_TYPE, payload },
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: markerDownloadUrl uses selectedFormatId directly, no need to add it as dependency
  useEffect(() => {
    setDownloadUrl(markerDownloadUrl());
  }, [selectedFormatId]);

  const filterVideoFormats = (formats: FormatEdge[]): FormatEdge[] => {
    return formats.filter((f) => f.node.mimetype.includes(VIDEO) || f.node.mimetype === 'application/mxf');
  };

  const { loading } = useQuery<AssetQueryResponse>(buildQuery(), {
    skip: !isOpen,
    onCompleted: (data) => {
      if (data.asset) {
        const permissions: AssetPermissions = JSON.parse(JSON.parse(data.asset.myAssetPermissions));
        if (!permissions.hires) {
          const noHires = data.asset.formatSet.edges.filter((format) => format.node.fmtType.slug !== HIRES_FORMAT_SLUG);
          setFormatSet(filterVideoFormats(noHires));
        } else {
          setFormatSet(filterVideoFormats(data.asset.formatSet.edges));
        }
      }
    },
    onError: (e) => {
      setError(e.message);
    },
  });

  const onClose = (): void => {
    viewStateVar({
      ...viewStateVar(),
      downloadMarkerDialog: { markerId: '', assetId: '' },
    });
  };

  // reset the selected marker
  // biome-ignore lint/correctness/useExhaustiveDependencies: cleanup effect only runs on unmount, onClose is stable
  useEffect(() => () => onClose(), []);

  const onFormatSelect = (e: SelectChangeEvent<string | null>): void => {
    const fnodeId = e.target.value as string;
    const fmt = fSet?.find((f) => f.node.id === fnodeId);
    const job = getFirstJob();
    setError(null);
    setSelectedFormatId(e.target.value as string);
    setSelectedFormat(fmt || null);
    if (fmt) {
      onFormatSelectedWorkflow(fmt, job);
    }
  };

  const onFormatSelectedWorkflow = (format: FormatEdge, job: JobEdge | undefined): void => {
    if (!format.node.presignable) {
      // if format not presignable and format downloadable
      // downloadStatus is 'ready'
      setDownloadStatus(READY);
      return;
    }

    if (!job?.node) {
      // if not job
      // set downloadStatus to 'job needed';
      setDownloadStatus(JOB_NEEDED);
      return;
    }

    // job attempt is made when this first loads
    if (job.node.status === 'complete') {
      // if job, check status:
      // if complete, set downloadStatus to 'job complete'
      setDownloadStatus(JOB_COMPLETE);
      return;
    }
    if (job.node.status === 'error') {
      // if job, check status:
      // if error , set downloadStatus to 'job needed'
      setDownloadStatus(JOB_NEEDED);
      return;
    }
    setDownloadStatus(JOB_NOT_COMPLETE);
  };

  const markerDownloadUrl = (): string => {
    const urlBase = `${window.location.origin}${API_PATH}/download/marker`;
    const url = new URL(urlBase);
    url.searchParams.set('format_id', selectedFormatId || '');
    url.searchParams.set('marker_id', viewState.downloadMarkerDialog.markerId);
    return url.toString();
  };

  const formControlLabelSx = {
    ml: 0,
    mb: '4px',
  };

  const getErrorMessage = (): string => {
    if (error) return error;
    if (actionError) return (actionError as ApolloError).message;
    if (jobError) return (jobError as ApolloError).message;
    return '';
  };

  // Determine the title based on state
  const getTitle = (): string => {
    if (actionError) return 'An error occurred';
    if (actionResponse) return 'Request Sent!';
    if (downloadStatus === JOB_NOT_COMPLETE) return 'Pending Request';
    return 'Download';
  };

  // Determine what action buttons to show
  const getActions = (): React.ReactNode | undefined => {
    if (loading || actionError || actionResponse || downloadStatus === JOB_NOT_COMPLETE || !fSet?.length) {
      return undefined;
    }
    return (
      <AkomiDialogActions variant="confirm">
        {downloadStatus === null && (
          <Button disabled={true} variant="contained" color="primary">
            ...
          </Button>
        )}
        {downloadStatus === JOB_NEEDED && (
          <Button disabled={!selectedFormatId} variant="contained" color="primary" onClick={onFireAction}>
            Request Subclip
          </Button>
        )}
        {downloadStatus === JOB_COMPLETE && (
          <Button disabled={!selectedFormatId} variant="contained" color="primary" onClick={onGetJobResult}>
            Download
          </Button>
        )}
        {downloadStatus === READY && (
          <Button
            disabled={!selectedFormatId}
            variant="contained"
            color="primary"
            href={downloadUrl || undefined}
            onClick={onClose}
          >
            Download
          </Button>
        )}
      </AkomiDialogActions>
    );
  };

  // Determine content based on state
  const getContent = (): React.ReactNode => {
    if (actionError) {
      return (
        <>
          <Box sx={formControlLabelSx}>Unable to submit this request due to the following errors:</Box>
          {actionError.message}
        </>
      );
    }

    if (!fSet?.length && !loading) {
      return <Box sx={formControlLabelSx}>No downloadable formats available</Box>;
    }

    if (actionResponse || downloadStatus === JOB_NOT_COMPLETE) {
      return (
        <Box sx={formControlLabelSx}>
          <Link to={'/jobs'}>Check the status</Link>
          <span> of your request</span>
        </Box>
      );
    }

    return (
      <>
        <Box sx={{ mb: 2 }}>
          <FormLabel sx={{ display: 'block', fontSize: '13px', color: 'text.secondary', mb: '6px' }}>
            Select a file format for marker download
          </FormLabel>
          <Select sx={{ width: '100%' }} value={selectedFormatId} onChange={onFormatSelect}>
            {fSet?.map((f) => (
              <MenuItem key={f.node.id} value={f.node.id} disabled={!f.node.online && !f.node.presignable}>
                {!f.node.online && !f.node.presignable && <OfflineBoltIcon sx={{ pr: '7px' }} />}
                {`${f.node.fmtType.name} (${f.node.filename})`}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Typography color="error">{getErrorMessage()}</Typography>
      </>
    );
  };

  return (
    <AkomiDialog
      open={isOpen}
      onClose={onClose}
      title={getTitle()}
      maxWidth="xs"
      loading={loading}
      skeletonRows={3}
      variant="form"
      actions={getActions()}
    >
      {getContent()}
    </AkomiDialog>
  );
};

export default DownloadMarkerModal;
